import { Link, useLocation } from 'react-router-dom'

import { Layout } from '@/components/layout'
import { ErrorViewer } from '@/components/util/ErrorViewer'

import { assertExhaustive } from '@/utils/assert'

import { useUser } from '@/entities/user/api/hooks'

import type { RundownSessionError } from './rundown-session'

/**
 * Full-page error for <RundownRoute/>` when the session fails to load.
 */
export function RundownRouteError({ error }: { error: RundownSessionError }) {
  switch (error.reason) {
    case 'not-found': {
      return <NotFoundError />
    }

    case 'archived': {
      return <ArchivedError />
    }

    case 'unauthorized': {
      return <UnauthorizedError />
    }

    case 'unknown': {
      return <UnknownError error={error} />
    }

    default: {
      return assertExhaustive(error)
    }
  }
}

function NotFoundError() {
  return (
    <Layout title="Not Found">
      <div className="space-y-6">
        <p className="font-medium">
          We couldn&apos;t find the rundown you&apos;re looking for.
        </p>

        <p className="text-gray-500">
          The link you followed might be incorrect or the rundown may have been
          deleted.
        </p>
      </div>
    </Layout>
  )
}

function ArchivedError() {
  return (
    <Layout title="Rundown Archived">
      <div className="space-y-6">
        <p className="font-medium">This rundown has been archived.</p>

        <p className="text-gray-500">
          Archived rundowns are no longer accessible to prevent accidental
          changes to past events.
        </p>
        <Link
          to="/dashboard"
          className="inline-block text-gray-500 underline hover:text-gray-400"
        >
          Go to Dashboard →
        </Link>
      </div>
    </Layout>
  )
}

function UnauthorizedError() {
  const location = useLocation()
  const { data: user } = useUser()

  // Differentiate message for signed-in users
  if (user) {
    return (
      <Layout title="Access Denied">
        <div className="space-y-6">
          <p className="font-medium">
            This account doesn&apos;t have access to this rundown.
          </p>

          <div className="text-gray-500 space-y-2">
            <p>
              {user.email ? `You are signed in as ${user.email}. ` : ''}Ask the
              rundown owner to invite you, or check that you opened the right
              link.
            </p>
            <Link
              to="/dashboard"
              className="inline-block underline hover:text-gray-400"
            >
              Go to Dashboard →
            </Link>
          </div>
        </div>
      </Layout>
    )
  }

  const next = encodeURIComponent(location.pathname + location.search)

  return (
    <Layout title="Access Denied">
      <div className="space-y-6">
        <p className="font-medium">
          You don&apos;t have permission to view this rundown.
        </p>

        <div className="text-gray-500 space-y-2">
          <p>This might be because:</p>
          <ul className="text-left">
            <li>• You need to log in to view this rundown</li>
            <li>• You were sent an incorrect link</li>
            <li>• The rundown owner hasn&apos;t given you access</li>
          </ul>
          <Link
            to={`/login?next=${next}`}
            className="underline"
          >
            Go to Login →
          </Link>
        </div>
      </div>
    </Layout>
  )
}

function UnknownError({
  error,
}: {
  error: Extract<RundownSessionError, { reason: 'unknown' }>
}) {
  return (
    <Layout title="Server Error">
      <div className="space-y-6">
        <p className="font-medium">Something went wrong on our end.</p>
        <div className="text-gray-500 space-y-2">
          <p>
            Don&apos;t worry, this is probably an issue on our end, not
            something you did.
          </p>
          <p>
            If this keeps happening, please contact us at{' '}
            <a
              href="mailto:hello@rundownstudio.app"
              className="underline hover:text-gray-400"
            >
              hello@rundownstudio.app
            </a>
          </p>
        </div>

        <ErrorViewer
          error={error.cause}
          meta={{ Status: String(error.status ?? '-') }}
        />
      </div>
    </Layout>
  )
}
